import { AsyncConstraints, Constraints, Dictionary, ValidationResult } from './types';

export const validate = <T extends Dictionary>(constraints: Constraints<T>, values: T) =>
  Object.keys(constraints).reduce<ValidationResult>((result, name) => {
    const validators = constraints[name] || [];
    const value = values[name];

    validators.forEach(validator => {
      const error = validator(value);

      if (error) {
        result[name] = error;
        return true;
      }

      return false;
    });

    return result;
  }, {});

export const validateAsync = async <T extends Dictionary>(
  constraints: AsyncConstraints<T>,
  values: T
) => {
  const errors = await Promise.all(
    Object.keys(constraints).map(async name => {
      const validators = constraints[name] || [];
      const value = values[name];

      const errors = await Promise.all(validators.map(validator => validator(value)));

      const firstError = errors.find(error => !!error);

      return firstError ? { [name]: firstError } : undefined;
    })
  );

  return (errors.filter(Boolean) as ValidationResult[]).reduce(
    (result, error) => ({
      ...result,
      ...error
    }),
    {} as ValidationResult
  );
};

export const validateAndTranslate = async (
  constraints: Constraints,
  values: Dictionary,
  t: (...props: any[]) => string
) => {
  const errors = await validate(constraints, values);
  return Object.keys(errors).reduce(
    (result, name) => ({
      ...result,
      [name]: t(errors[name].key, errors[name].options)
    }),
    {}
  );
};

export const createValidator =
  <T extends Dictionary>(constraints: Constraints<T>) =>
  (values: T) =>
    validate(constraints, values);

export const createAsyncValidator =
  <T extends Dictionary>(constraints: AsyncConstraints<T>) =>
  (values: T) =>
    validateAsync(constraints, values);
